<ph-card flat class="ap-card">
    <ng-container *ngIf="finalState?.kind === 'cancelled'">
        <ph-stack gap="tight">
            <ph-toolbar justify="between" nowrap>
                <ph-label>{{ headerTitle }}</ph-label>
                <ph-status-badge status="failed" variant="light">Rejected</ph-status-badge>
            </ph-toolbar>
            <ph-hint class="ap-card-footer-rejected">Rejected. No changes were committed.</ph-hint>
        </ph-stack>
    </ng-container>

    <ng-container *ngIf="finalState?.kind === 'skipped'">
        <ph-stack gap="tight" class="ap-card-skipped">
            <ph-toolbar justify="between" nowrap>
                <ph-label>{{ headerTitle }}</ph-label>
                <ph-status-badge status="disabled" variant="light">Skipped</ph-status-badge>
            </ph-toolbar>
            <ph-hint>Skipped when you moved on. No changes were committed.</ph-hint>
        </ph-stack>
    </ng-container>

    <ng-container *ngIf="finalState?.kind === 'committed'">
        <ph-alert status="success" variant="lighter" size="small">{{ completedLabel }}</ph-alert>
        <ph-actions align="start" class="ap-card-links" *ngIf="destinationRoute || showRecordLinks">
            <ph-button variant="secondary" mode="filled" size="small" type="button"
                *ngIf="destinationRoute && !showRecordLinks"
                class="ap-card-view-created"
                trailing-icon="arrow-up-right"
                [routerLink]="destinationRoute"
                [queryParams]="destinationQuery">
                {{ destinationLabel }}
            </ph-button>
            <ng-container *ngIf="showRecordLinks">
                <ph-button variant="secondary" mode="filled" size="small" type="button"
                    *ngFor="let link of visibleRecordLinks; trackBy: trackByRecordRoute"
                    class="ap-card-view-created"
                    trailing-icon="arrow-up-right"
                    [routerLink]="link.route">
                    {{ link.label }}
                </ph-button>
                <ph-button variant="secondary" mode="filled" size="small" type="button"
                    *ngIf="hiddenRecordLinkCount > 0"
                    class="ap-record-links-more"
                    (click)="onShowAllRecordLinks()">
                    Show {{ hiddenRecordLinkCount }} more
                </ph-button>
            </ng-container>
        </ph-actions>

        <ph-stack gap="tight" class="ap-vendor-link" *ngIf="vendorAccessLink as link">
            <ph-toolbar nowrap>
                <span class="ap-vendor-link-url">{{ link.url }}</span>
                <ph-button variant="secondary" mode="filled" size="small" type="button" class="ap-vendor-link-copy"
                    icon="copy" [copyToClipboard]="link.url"
                    copySuccessMessage="The secure vendor link is on your clipboard.">Copy link</ph-button>
            </ph-toolbar>
            <ph-hint *ngIf="link.expiresAtLabel">Expires {{ link.expiresAtLabel }}</ph-hint>
            <ph-hint>Copy it now; it is not shown again.</ph-hint>
        </ph-stack>
    </ng-container>


    <ng-container *ngIf="finalState === null">
        <ph-stack *ngIf="hasBodyContent">
            <ph-toolbar justify="between" class="ap-context-row" *ngIf="contextRowVisible">
                <ph-hint *ngIf="contextRecord">{{ contextRecord }}</ph-hint>
                <ph-button variant="secondary" mode="filled" size="small" type="button"
                    *ngIf="contextLink as link"
                    class="ap-context-link"
                    trailing-icon="arrow-square-out"
                    [routerLink]="link.route">
                    {{ link.label }}
                </ph-button>
            </ph-toolbar>

            <ng-container *ngFor="let section of bodySections; let i = index; trackBy: trackBySectionIndex">
                <div class="ap-section" [attr.data-kind]="section.kind">
                <ng-container [ngSwitch]="section.kind">
                    <app-ap-header-section
                        *ngSwitchCase="'header'"
                        [section]="$any(section)"
                        [scaleLine]="scaleLine">
                    </app-ap-header-section>

                    <app-ap-status-picker-section
                        *ngSwitchCase="'status_picker'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.status_picker ?? null"
                        (edit)="onStatusEdit($event)">
                    </app-ap-status-picker-section>

                    <app-ap-email-template-section
                        *ngSwitchCase="'email_template'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.email_template ?? null"
                        (edit)="onEmailEdit($event)">
                    </app-ap-email-template-section>

                    <app-ap-toggle-section
                        *ngSwitchCase="'toggle'"
                        [section]="$any(section)"
                        [pendingValue]="toggleValue($any(section).key)"
                        [disabled]="finalState !== null || submitting"
                        (edit)="onToggleEdit($event)">
                    </app-ap-toggle-section>

                    <app-ap-recipients-section
                        *ngSwitchCase="'recipients'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.recipients ?? null"
                        (edit)="onRecipientsEdit($event)"
                        (listAction)="onRecipientsListAction($event)">
                    </app-ap-recipients-section>

                    <app-ap-recipients-people-section
                        *ngSwitchCase="'recipients_people'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.recipients ?? null"
                        (edit)="onRecipientsEdit($event)">
                    </app-ap-recipients-people-section>

                    <app-ap-impact-summary-section
                        *ngSwitchCase="'impact_summary'"
                        [section]="$any(section)"
                        [primaryOverride]="impactSummaryPrimaryOverride">
                    </app-ap-impact-summary-section>

                    <app-ap-key-value-section
                        *ngSwitchCase="'key_value'"
                        [section]="$any(section)">
                    </app-ap-key-value-section>

                    <app-ap-info-block-section
                        *ngSwitchCase="'info_block'"
                        [section]="$any(section)">
                    </app-ap-info-block-section>

                    <app-ap-table-section
                        *ngSwitchCase="'table'"
                        [section]="$any(section)">
                    </app-ap-table-section>

                    <ng-container *ngSwitchCase="'deep_link'">
                        <!-- Skipped when hoisted into the context row (A2). -->
                        <app-ap-deep-link-section
                            *ngIf="!contextRowVisible"
                            [section]="$any(section)">
                        </app-ap-deep-link-section>
                    </ng-container>

                    <app-ap-outcome-section
                        *ngSwitchCase="'outcome'"
                        [section]="$any(section)"
                        [confidence]="confidence"
                        [effectOverride]="outcomeEffectOverride">
                    </app-ap-outcome-section>

                    <app-ap-why-section
                        *ngSwitchCase="'why'"
                        [section]="$any(section)">
                    </app-ap-why-section>

                    <app-ap-guardrail-chips-section
                        *ngSwitchCase="'guardrail_chips'"
                        [section]="$any(section)">
                    </app-ap-guardrail-chips-section>

                    <app-ap-choice-section
                        *ngSwitchCase="'choice'"
                        [section]="$any(section)"
                        [pendingEdit]="pendingEdits.choice ?? null"
                        [confidence]="confidence"
                        (edit)="onChoiceEdit($event)">
                    </app-ap-choice-section>

                    <ph-alert *ngSwitchDefault class="ap-section-placeholder" status="warning" variant="lighter" size="small">
                        {{ section.kind }}
                        <span *ngIf="!isKnownKind(section)" class="ap-section-placeholder-note">
                            Unknown section kind. The backend may have sent a newer section type than this client knows about.
                        </span>
                    </ph-alert>
                </ng-container>
                </div>
            </ng-container>
        </ph-stack>

        <ph-alert *ngIf="viewerCannotPerform" class="ap-viewer-blocked" status="information" variant="lighter" size="small">
            {{ viewerCapability?.reason }}<ng-container *ngIf="viewerCannotPerformDetail"> {{ viewerCannotPerformDetail }}</ng-container>
        </ph-alert>

        <ng-container *ngIf="!hideActions && payload?.preview?.actions as actions">
            <ph-actions slot="footer" align="between" class="ap-card-actions">
                <ph-actions align="start">
                    <ph-button
                        type="button"
                        size="small"
                        class="ap-primary-button"
                        [icon]="!submitting && actions.primary.icon === 'check' ? 'check' : null"
                        [disabled]="submitting || snoozing || isPrimaryDisabled"
                        [attr.title]="isPrimaryDisabled ? primaryDisabledReason : null"
                        (click)="onConfirm()">
                        {{ submitting ? 'Saving…' : primaryButtonLabel }}
                    </ph-button>
                    <ph-button
                        type="button"
                        size="small"
                        class="ap-destructive-button"
                        variant="secondary"
                        mode="filled"
                        [disabled]="submitting || snoozing"
                        (click)="onCancel()">
                        {{ actions.destructive.label }}
                    </ph-button>
                </ph-actions>
                <ph-button
                    *ngIf="canSnooze"
                    variant="secondary"
                    mode="ghost"
                    size="small"
                    class="ap-snooze-button"
                    [disabled]="submitting || snoozing"
                    (click)="onSnooze()">
                    {{ snoozing ? 'Snoozing…' : 'Snooze until tomorrow' }}
                </ph-button>
            </ph-actions>
        </ng-container>
    </ng-container>
</ph-card>
